/**
 * ChatView Component
 *
 * Composite component combining ConversationHeader + MessageList + ChatInput.
 * Manages reply state, loads messages, subscribes to realtime,
 * handles send/edit/delete.
 *
 * @module components/chat/ChatView
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  getMessages,
  getConversation,
  getParticipants,
  sendMessage,
  uploadChatAttachment,
  subscribeToMessages,
  markConversationRead,
  deleteConversation,
  leaveConversation,
  muteConversation,
  deleteMessage,
  editMessage,
  shareMessageToBitacora,
} from '../../services/chatService';
import ConversationHeader from './ConversationHeader';
import MessageList from './MessageList';
import SharedChatInput from './ChatInput';
import BitacoraLinkModal from './BitacoraLinkModal';
import './chat.css';

/**
 * Cadencia del refresco de `chat_participants.last_read_at` (#509). Igual a la
 * de `MessagesPage` (`refetchInterval: 1000 * 20`) para que el doble check azul
 * se mueva a la misma velocidad en los dos carriles.
 */
const PARTICIPANTS_REFRESH_MS = 20000;

const ChatView = ({ conversationId, onBack, onConversationDeleted }) => {
  const { user } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showBitacoraModal, setShowBitacoraModal] = useState(false);
  const subscriptionRef = useRef(null);

  // Load conversation details and messages
  const loadConversation = useCallback(async () => {
    if (!conversationId) return;

    setLoading(true);
    try {
      const [convResult, partResult, msgResult] = await Promise.all([
        getConversation(conversationId),
        getParticipants(conversationId),
        getMessages(conversationId, 50),
      ]);

      if (convResult.data) {
        // Enrich with DM participant info
        const conv = convResult.data;
        if (conv.type === 'direct' && partResult.data) {
          const other = partResult.data.find(p => p.user?.id !== user?.id);
          conv.dmParticipant = other?.user || null;
        }
        setConversation(conv);
      }

      if (partResult.data) setParticipants(partResult.data);

      if (msgResult.data) {
        setMessages(msgResult.data);
        setHasMore(msgResult.hasMore);
      }

      // Mark as read
      await markConversationRead(conversationId);
    } catch (err) {
      console.error('Error loading conversation:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId, user?.id]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  // -------------------------------------------------------------------
  // Acuses de lectura (doble check azul) — refresco periódico. #509
  //
  // El acuse NO vive en `chat_messages` (no hay `is_read` por mensaje, ver
  // #446): se deriva de `chat_participants.last_read_at` de los OTROS
  // participantes — que es lo que `getParticipants` trae. Y `last_read_at`
  // cambia cuando el otro ABRE la conversación, un evento que no produce
  // ningún INSERT en `chat_messages`, así que la suscripción realtime de
  // abajo no se entera nunca.
  //
  // `loadConversation` sólo se re-ejecuta al cambiar `conversationId`, así
  // que sin esto el técnico veía los checks congelados en el estado que
  // tenían al abrir el chat, para siempre. `MessagesPage` (el carril del
  // escritorio) ya refrescaba participantes cada 20s vía react-query
  // (`MessagesPage.js`, queryKey `conversation-participants`); esto es la
  // misma cadencia para el carril del técnico (`ProjectChatPage` ->
  // `ChatView`), que era el único que quedaba congelado.
  //
  // Sólo se refrescan los PARTICIPANTES: los mensajes ya llegan por
  // realtime, y volver a pedirlos cada 20s sería la query redundante que
  // este mismo issue vino a sacar.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!conversationId) return undefined;

    let cancelled = false;
    const refreshParticipants = async () => {
      const { data } = await getParticipants(conversationId);
      if (!cancelled && data) setParticipants(data);
    };

    const timer = setInterval(refreshParticipants, PARTICIPANTS_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [conversationId]);

  // Realtime subscription
  //
  // #447: also handles UPDATE (edit / soft-delete via `is_deleted = true`)
  // by replacing the message in place instead of only ever appending new
  // ones — previously another participant editing or deleting a message
  // here only showed up after a reload.
  useEffect(() => {
    if (!conversationId) return;

    subscriptionRef.current = subscribeToMessages(conversationId, (message, eventType) => {
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === message.id);
        if (idx === -1) {
          if (eventType === 'UPDATE') return prev;
          return [...prev, message];
        }
        const next = [...prev];
        next[idx] = message;
        return next;
      });
      // Mark as read since user is viewing (only for genuinely new messages)
      if (eventType !== 'UPDATE') {
        markConversationRead(conversationId);
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [conversationId]);

  // Load more messages (infinite scroll)
  const handleLoadMore = useCallback(async () => {
    if (!conversationId || !hasMore || loading) return;

    const oldestMessage = messages[0];
    if (!oldestMessage) return;

    const { data, hasMore: more } = await getMessages(conversationId, 50, {
      before: oldestMessage.created_at,
    });

    if (data) {
      setMessages(prev => [...data, ...prev]);
      setHasMore(more);
    }
  }, [conversationId, hasMore, loading, messages]);

  // Send message (with optional attachment: image OR document, #451)
  const handleSend = useCallback(async (content, attachedFile) => {
    if (!conversationId) return;

    setSending(true);
    try {
      let attachments = [];
      let messageType = 'text';

      // Upload the attachment if provided
      if (attachedFile) {
        const isImage = attachedFile.type?.startsWith('image/');
        const uploadResult = await uploadChatAttachment(attachedFile, conversationId);
        if (uploadResult.error) {
          throw new Error(isImage ? 'Error al subir la imagen' : 'Error al subir el archivo');
        }
        attachments = [{
          url: uploadResult.url,
          type: attachedFile.type,
          name: attachedFile.name,
          size: attachedFile.size,
        }];
        // `chat_messages.message_type` CHECK allows 'image' and 'file' — pick
        // the one that matches so MessageBubble renders the right affordance.
        messageType = isImage ? 'image' : 'file';
      }

      const payload = {
        conversation_id: conversationId,
        content: content || null,
        message_type: messageType,
        attachments,
        reply_to_id: replyTo?.id || null,
      };

      const { error } = await sendMessage(payload);

      if (error) throw error;

      setReplyTo(null);
    } catch (err) {
      toast.error(err.message || 'Error al enviar mensaje');
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  }, [conversationId, replyTo]);

  // Reply handler
  const handleReply = useCallback((message) => {
    setReplyTo(message);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  // Share a single message to the project bitacora (channel conversations only)
  const handleShareToBitacora = useCallback(async (message) => {
    const projectId = conversation?.project_id;
    if (!projectId) {
      toast.error('Esta conversación no es de un proyecto');
      return;
    }
    const ok = window.confirm('¿Compartir este mensaje en la bitácora del proyecto?');
    if (!ok) return;

    const { error } = await shareMessageToBitacora(message, { projectId });
    if (error) {
      toast.error(error.message || 'No se pudo compartir en bitácora');
      return;
    }
    toast.success('Compartido en bitácora');
    const sharedAt = new Date().toISOString();
    setMessages(prev => prev.map(m => (
      m.id === message.id
        ? { ...m, metadata: { ...(m.metadata || {}), shared_to_bitacora_at: sharedAt } }
        : m
    )));
  }, [conversation]);

  // Edit a single message (own messages, 15-min window — #449). Updates local
  // state directly with the server-confirmed row rather than waiting on the
  // realtime UPDATE round-trip, so the change is snappy for the editor; the
  // #447 realtime UPDATE handler above still keeps OTHER participants in sync.
  const handleEditMessage = useCallback(async (message, newContent) => {
    const { data, error } = await editMessage(message.id, newContent);
    if (error) {
      toast.error(error.message || 'No se pudo editar el mensaje');
      throw error;
    }
    setMessages(prev => prev.map(m => (m.id === message.id ? { ...m, ...data } : m)));
  }, []);

  // Delete a single message (sender only, enforced server-side too)
  const handleDeleteMessage = useCallback(async (message) => {
    const ok = window.confirm('¿Eliminar este mensaje?');
    if (!ok) return;

    const { error } = await deleteMessage(message.id);
    if (error) {
      toast.error(error.message || 'No se pudo eliminar el mensaje');
      return;
    }
    setMessages(prev => prev.filter(m => m.id !== message.id));
    toast.success('Mensaje eliminado');
  }, []);

  // Delete conversation
  const handleDelete = useCallback(async () => {
    if (!conversationId) return;
    const { success, error } = await deleteConversation(conversationId);
    if (success) {
      toast.success('Conversación eliminada');
      onConversationDeleted?.();
    } else {
      toast.error(error?.message || 'Error al eliminar');
    }
  }, [conversationId, onConversationDeleted]);

  // Leave conversation
  const handleLeave = useCallback(async () => {
    if (!conversationId) return;
    const { success, error } = await leaveConversation(conversationId);
    if (success) {
      toast.success('Saliste de la conversación');
      onConversationDeleted?.();
    } else {
      toast.error(error?.message || 'Error al salir');
    }
  }, [conversationId, onConversationDeleted]);

  // Mute/unmute conversation
  const handleMute = useCallback(async () => {
    if (!conversationId || !conversation) return;
    const newMuted = !conversation.isMuted;
    const { success } = await muteConversation(conversationId, newMuted);
    if (success) {
      setConversation(prev => ({ ...prev, isMuted: newMuted }));
      toast.success(newMuted ? 'Conversación silenciada' : 'Notificaciones activadas');
    }
  }, [conversationId, conversation]);

  const canShareToBitacora = !!conversation
    && conversation.type === 'channel'
    && !!conversation.project_id;

  // Empty state when no conversation selected
  if (!conversationId) {
    return (
      <div className="chat-main-panel d-flex align-items-center justify-content-center">
        <div className="text-center text-muted">
          <i className="bi bi-chat-left-text" style={{ fontSize: '3rem', opacity: 0.3 }} />
          <p className="mt-3">Selecciona una conversación para comenzar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-main-panel">
      <ConversationHeader
        conversation={conversation}
        participants={participants}
        onBack={onBack}
        onMute={handleMute}
        onLinkBitacora={() => setShowBitacoraModal(true)}
        onDelete={handleDelete}
        onLeave={handleLeave}
      />

      <MessageList
        messages={messages}
        currentUserId={user?.id}
        participants={participants}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onReply={handleReply}
        onShareToBitacora={handleShareToBitacora}
        onDelete={handleDeleteMessage}
        onEdit={handleEditMessage}
        canShareToBitacora={canShareToBitacora}
      />

      <SharedChatInput
        onSend={handleSend}
        sending={sending}
        replyTo={replyTo}
        onCancelReply={handleCancelReply}
      />

      {conversation && (
        <BitacoraLinkModal
          show={showBitacoraModal}
          onHide={() => setShowBitacoraModal(false)}
          conversation={conversation}
          onLinked={() => toast.success('Vinculado exitosamente')}
        />
      )}
    </div>
  );
};

ChatView.propTypes = {
  conversationId: PropTypes.string,
  onBack: PropTypes.func,
  onConversationDeleted: PropTypes.func,
};

export default ChatView;
